import type { CertificateGroupDivisibility, CertificateStatus } from 'types'

/**
 * Transfer states that indicate a certificate is currently being transferred.
 *
 * WHY THE PLATFORM OFFERS `Cancel transfer` AT ONLY ONE OF THESE FOUR.
 *
 * This used to carry a `TODO(VERA-455)` — duplicated verbatim in CertificatesPage
 * and CertificatePage — claiming that a certificate in the other three states
 * "shows neither Transfer nor Cancel transfer: a dead end", and that widening the
 * platform's Cancel needed confirmation the API would accept it. The API settles
 * it, and the answer is the opposite: the current scoping is CORRECT, not a gap.
 * (The identical paragraph living in two files is exactly how a wrong comment
 * survives, so the reasoning lives here once and the call sites point at it.)
 *
 * 1. The brand's cancel is scoped to the brand's OWN transfer.
 *    `cancel-store-initiated-transfer.use-case.ts` requires ALL THREE of: certificate
 *    status `WaitingAccept`, a transfer at `TransferStatus.Confirmation`, and
 *    `transferEntity.type === TransferType.Primary`. It throws otherwise.
 *
 * 2. A PRIMARY transfer only ever sits at `waiting_accept`. The other three states
 *    are written exclusively by the customer-to-customer flow:
 *      - `transfer_initiated`            <- transfer-certificate-between-owners-init
 *      - `transfer_waiting_confirmation` <- scan-processor.service `scanTransferStep`
 *                                           (the SCAN that advances a peer transfer)
 *      - `cancel_request`                <- request-cancel-customer-initiated-transfer-by-seller
 *    Note the implication runs ONE WAY only: `waiting_accept` does NOT imply primary.
 *    A peer-to-peer transfer also lands there (between-owners-confirm, and
 *    reject-incoming-transfer-cancel-request-by-buyer). That is precisely why both
 *    the API guard above and the UI's `row?.transfer?.type === 'primary'` check the
 *    TYPE as well as the status — neither check is redundant.
 *
 * 3. None of the other three states is a dead end; each has a cancel path, all of
 *    them on the CUSTOMER controller (`transfer.customer.controller.ts`):
 *      - `POST cancel/:certificateId`          accepts transfer_initiated | transfer_waiting_confirmation
 *      - `POST request-cancel/:certificateId`  accepts waiting_accept
 *      - `POST confirm-cancel-request/:id` and `POST reject-cancel-request/:id` act on cancel_request
 *    `apps/app` wires all four (`store/certificates/saga.tsx`, plus the giftIn /
 *    giftOut action sections).
 *
 * So a brand showing neither Transfer nor Cancel during a customer-to-customer
 * transfer is correct scoping: the brand is a BYSTANDER to two customers moving an
 * item it once sold, and those two customers own the cancel.
 *
 * Widening the platform's Cancel to the other three states would mean letting a
 * brand unilaterally cancel a customer's gift. That is a product decision nobody
 * has made — NOT a missing feature. Do not "fix" it without one.
 */
export const TRANSFER_STATES: CertificateStatus[] = [
  'transfer_initiated',
  'transfer_waiting_confirmation',
  'waiting_accept',
  'cancel_request',
]

/**
 * Checks if a certificate is currently in a transfer state
 * @param status - The certificate status to check
 * @returns true if the certificate is in a transfer state, false otherwise
 */
export const isCertificateInTransferState = (status: CertificateStatus | string | null | undefined): boolean => {
  if (!status) return false
  return TRANSFER_STATES.includes(status as CertificateStatus)
}

/**
 * Gets a user-friendly message explaining why a certificate cannot be transferred
 * @param status - The certificate status
 * @returns A message explaining why transfer is not allowed, or null if transfer is allowed
 */
export const getTransferDisabledMessage = (status: CertificateStatus | string | null | undefined): string | null => {
  if (!status) return null
  
  if (isCertificateInTransferState(status)) {
    switch (status) {
      case 'transfer_initiated':
        return 'This certificate is already being transferred'
      case 'transfer_waiting_confirmation':
        return 'This certificate is waiting for transfer confirmation'
      case 'waiting_accept':
        return 'This certificate is waiting for acceptance'
      case 'cancel_request':
        return 'This certificate has a pending transfer cancellation request'
      default:
        return 'This certificate is already in a transfer state'
    }
  }

  return null
}

/**
 * The same blocking reason as `getTransferDisabledMessage`, phrased so it can be
 * COUNTED: a bare predicate with no subject, so "7 waiting for acceptance" reads
 * correctly where "7: This certificate is waiting for acceptance" does not.
 *
 * Lives here, beside the sentence form, rather than in the modal that needed it —
 * the two are renderings of one fact, and putting the plural phrasing at the call
 * site is exactly how a surface ends up with its own private wording that drifts
 * from the canonical one (§44 in spirit).
 *
 * Returns null when the status does not block a transfer.
 */
/**
 * The tooltip on a Transfer affordance the user lacks permission for (§46: permission
 * disables + explains, record state hides).
 *
 * One constant because there are SEVEN of these — two list kebabs, the bulk bar, the
 * owner-detail button, the certificate-detail kebab, the cancel-transfer kebab and the
 * certificates page header — and they were seven hand-typed copies of the same sentence.
 * §25 requires the permission's own name here, so if that label is ever retitled this
 * string must change with it; seven copies is seven chances to update six.
 */
export const TRANSFER_PERMISSION_TOOLTIP =
  "You need the 'Transfer certificate' permission. Ask an admin to grant it."

/**
 * VERA-931 / VRG-528 — confirming arrival is its OWN permission, so it needs its own
 * tooltip: pointing someone at 'Transfer certificate' when that is not what gates the
 * button is worse than no tooltip, because they would ask an admin for the wrong grant.
 *
 * The quoted name matches the label in the permissions matrix (§25, §27) — retitle one and
 * this string must change with it.
 */
export const CUSTODY_RECEIVE_PERMISSION_TOOLTIP =
  "You need the 'Confirm arrival' permission. Ask an admin to grant it."

export const getTransferBlockedReason = (
  status: CertificateStatus | string | null | undefined,
): string | null => {
  if (!status) return null

  switch (status) {
    case 'transfer_initiated':
    case 'transfer_waiting_confirmation':
      return 'already being transferred'
    case 'waiting_accept':
      return 'waiting for acceptance'
    case 'cancel_request':
      // "with a", not "have a". Every reason here is rendered as `${count} ${reason}`,
      // so it must agree with BOTH "1" and "2". A verb phrase cannot: "1 have a pending
      // transfer cancellation" is ungrammatical, and a count of 1 is the common case.
      // The others are already participles or adjectives ("waiting for acceptance",
      // "already activated"), which agree with any number; this was the one outlier.
      return 'with a pending transfer cancellation'
    case 'inactive':
      // Not blocked — the one status the endpoint accepts.
      return null
    case 'active':
      return 'already activated'
    case 'lost':
      return 'reported lost'
    case 'stolen':
      return 'reported stolen'
    case 'created':
      // Provisioned but not yet issued to the shelf — the state before `inactive`.
      return 'not issued yet'
    default:
      /**
       * A status this bundle does not know about — a row written by a newer API.
       *
       * Says only that it cannot move, and does not guess why. The previous default,
       * "already activated or no longer on the shelf", covered `active`, `lost`,
       * `stolen` and `created` with one phrase whose "or" admitted it did not know
       * which — so an operator reading "2 already activated or no longer on the
       * shelf" could not tell whether to go find two bags or file two police reports.
       * Every status the enum actually contains now names itself above; this branch
       * is only for the ones that do not exist yet, where declining to guess is the
       * whole point.
       */
      return 'in a status that cannot be transferred'
  }
}

/**
 * Has this certificate been activated — i.e. has it left the brand's shelf?
 *
 * Mirrors the server's `PackageCertificateService.isActivated()`
 * (`apps/api/src/application/services/package-certificate.service.ts`), which is
 * `is_active === true || status !== Inactive`. A primary transfer completing is what
 * activates a certificate, and the three primary-transfer CANCEL paths return it to the
 * pre-sale state — so "not activated" is precisely "still on the shelf".
 *
 * Two deliberate choices:
 * - `is_active` is checked first because the two columns are written together but stored
 *   independently, and a row where they DISAGREE must read as live, not as shelf stock.
 *   The platform's `Certificate` type does not carry `is_active` today, so this is a
 *   forward-compatible read of an optional field rather than a hard dependency on it.
 * - An ABSENT or unrecognised status returns `true` (activated). The failure mode this
 *   guards is asserting that a brand owns something it has already sold, so an unknown
 *   state must never be read as "the brand still holds it".
 */
export const isCertificateActivated = (
  certificate:
    | { status?: CertificateStatus | string | null; is_active?: boolean | null }
    | null
    | undefined,
): boolean => {
  if (!certificate) return true
  if (certificate.is_active === true) return true
  return certificate.status !== 'inactive'
}

/**
 * Is this certificate still held by the brand that issued it?
 *
 * True only for the knowable, expected pre-sale state: NO owner AND not activated. That
 * combination is what lets a surface name the company instead of rendering a blank that
 * reads as missing data — owners are people (the `owners` table) and a brand is a company,
 * so there is no schema path for a company to hold `certificates.owner_id` and the absence
 * is structural, not a gap.
 *
 * Deliberately NOT true for "activated with no owner". That state is genuinely anomalous
 * (it was sold, and the owner row is missing or unregistered), and naming the company there
 * would have the platform assert a false ownership claim about an item already in a
 * consumer's hands — strictly worse than the blank it would replace.
 *
 * Display-only. This does not, and must not, change ownership data. The deeper fix — the
 * brand as a first-class origin link in the provenance chain — belongs to VRG-319.
 */
export const isBrandHeldCertificate = (
  certificate:
    | {
        status?: CertificateStatus | string | null
        is_active?: boolean | null
        owner_id?: string | null
        customer_id?: string | null
        customer?: unknown | null
      }
    | null
    | undefined,
): boolean => {
  if (!certificate) return false
  const hasOwner = Boolean(
    certificate.owner_id || certificate.customer_id || certificate.customer,
  )
  if (hasOwner) return false
  return !isCertificateActivated(certificate)
}

/**
 * VRG-231 review round 2 item 3 — mirrors the server's own
 * `UnlinkCertificatesFromGroupUseCase.assertIndivisibleGroupIsSplittable`
 * (apps/api/src/application/use-cases/certificate/unlink-certificates-from-group/
 * unlink-certificates-from-group.use-case.ts): an indivisible group's members are
 * frozen together UNLESS every one of them — the tagless parent AND every child —
 * has never gone live (`created`/`inactive`) and has no owner. Once any member has
 * activated or been owned, the group is permanent.
 */
const NOT_YET_LIVE_STATUSES: (CertificateStatus | string)[] = ['inactive', 'created']

export type CertificateGroupSplittabilityMember = {
  status?: CertificateStatus | string | null
  owner_id?: string | null
  customer_id?: string | null
  customer?: unknown | null
}

/**
 * True when EVERY member (parent + children) is still not-yet-live and unowned —
 * i.e. an indivisible group made up of exactly these members could be split/
 * dissolved right now. Empty/absent membership is never splittable — that reads
 * as "we don't know the group's members yet", and §46 says hide (not show) an
 * action under uncertainty.
 */
export const isCertificateGroupSplittable = (
  members: CertificateGroupSplittabilityMember[] | null | undefined,
): boolean => {
  if (!members?.length) return false
  return members.every(
    member =>
      NOT_YET_LIVE_STATUSES.includes(member.status ?? '') &&
      !(member.owner_id || member.customer_id || member.customer),
  )
}

/**
 * The single answer every "Split group" / "Remove from group" call site needs:
 * can this group be split at all? A DIVISIBLE group always can — this returns
 * `true` unconditionally and without even looking at `groupData`, so a caller
 * never has to wait on a group fetch to show a divisible group's action. An
 * INDIVISIBLE group can only if `isCertificateGroupSplittable` above says so.
 */
export const canSplitCertificateGroup = (
  divisibility: CertificateGroupDivisibility | string | null | undefined,
  groupData:
    | {
        parent?: CertificateGroupSplittabilityMember | null
        children?: CertificateGroupSplittabilityMember[] | null
      }
    | null
    | undefined,
): boolean => {
  if (divisibility === 'divisible') return true
  if (divisibility !== 'indivisible') return false
  const members = [groupData?.parent, ...(groupData?.children ?? [])].filter(
    (member): member is CertificateGroupSplittabilityMember => Boolean(member),
  )
  return isCertificateGroupSplittable(members)
}

// ─────────────────────────────────────────────────────────────────────────────
// ROW-ACTION VISIBILITY — one definition per action, for every kebab that offers it
// ─────────────────────────────────────────────────────────────────────────────
//
// VRG-231 review round 5. These predicates existed as FOUR hand-written copies —
// the certificates list's row kebab, that list's group-member kebab, the
// certificate page's header actions, and that page's group-member kebab — and
// two of the four had drifted. The drift was not in which actions were offered;
// it was in WHEN. So the shared thing is the predicate, not the action list.
//
// What the drift actually was, because it is the argument for this module:
// both GROUP-MEMBER copies gated Transfer on `status === 'active'` while both
// non-group copies (and `main`) gate it on `status === 'inactive'`. Inverted.
// And inverted in the direction that breaks the product's DEFAULT configuration:
// the default activation trigger is `ON_PRIMARY_TRANSFER` ("On sale —
// certificates activate when the customer claims ownership"), so under that
// setting the transfer is WHAT ACTIVATES the certificate. Requiring `active`
// first is circular: the certificate can never activate, because it can never
// be transferred. A group member was permanently untransferable and nothing
// failed — no type error, no test, the menu item simply was not there.
//
// The convention this establishes, and the reason it is code rather than a
// paragraph: a kebab's action list may legitimately differ by context (a group
// member has no Archive; a tagless parent has no Replace tag), but the CONDITION
// under which a given action is available is a property of the certificate, not
// of the surface rendering it. Anything that varies by surface is passed in as
// an argument, so a caller cannot express a per-surface answer to a question
// that does not have one. New action, new predicate here — never a fifth copy.

/**
 * Transfer — offered on a certificate that has not yet gone to an owner.
 *
 * ⚠️ `inactive`, NOT `active`. See the module note above: the default activation
 * trigger is ON_PRIMARY_TRANSFER, so the transfer is what activates. Gating on
 * `active` is circular and silently makes Transfer unreachable forever.
 */
export const isTransferAvailable = (
  status: CertificateStatus | string | null | undefined,
): boolean => status === 'inactive'

/** Activate — a certificate that has never gone live. */
export const isActivateAvailable = (
  status: CertificateStatus | string | null | undefined,
): boolean => status === 'inactive' || status === 'created'

/**
 * Cancel transfer — only while a store-initiated transfer is genuinely pending
 * acceptance. This is the RESOLVING action for the state that hides Transfer
 * (§46: hiding an action creates an obligation to offer the way out of it).
 */
export const isCancelTransferAvailable = (
  status: CertificateStatus | string | null | undefined,
): boolean => status === 'waiting_accept'

/**
 * Replace tag — a not-yet-live certificate whose tag can still be swapped.
 *
 * `isTaglessParent` is the one genuinely per-surface input, and it is a fact
 * about the ROW rather than the surface: a certificate group's parent is tagless
 * by construction, so there is no tag to replace. Passed in because only the
 * caller knows whether it has resolved group membership for this row.
 */
export const isReplaceTagAvailable = (
  status: CertificateStatus | string | null | undefined,
  isTaglessParent = false,
): boolean => status === 'inactive' && !isTaglessParent

/**
 * Delete — a certificate that never went live AND was never owned.
 *
 * Ownership is read from `owner_id`/`customer_id`/`customer` together because
 * the payload populates them inconsistently across endpoints, and any one of
 * them alone has produced a wrong answer before.
 */
export const isDeleteAvailable = (certificate: {
  status?: CertificateStatus | string | null
  owner_id?: string | null
  customer_id?: string | null
  customer?: unknown | null
}): boolean =>
  certificate.status === 'inactive' &&
  !(certificate.owner_id || certificate.customer_id || certificate.customer)
